import { Fragment, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Dropdown, DropdownItem, DropdownToggle } from 'reactstrap'
import { mutations, queries } from '../../../../../api'
import { ChatType } from '../../../../../constants'
import { useAppSelector } from '../../../../../redux/hooks'
import {
  setRecentChatBlockedStatus,
  setRecentChatFavoriteStatus,
  setSelectedUser,
  toggleArchiveChat,
  toggleMuteChat,
} from '../../../../../redux/reducers/messenger/chatSlice'
import { SvgIcon } from '../../../../../shared/icons'
import { RecentChat } from '../../../../../types/components/chat'
import MuteChatModal from '../../../chat/chat-area/chat-header/contact/MuteChatModal'
import { getDropdownOptions } from './DropdownOption'
import DropdownWrapper from '../../../../../shared/DropdownWrapper'
import { getEventPath, getShadowRoot } from '../../../../../Webcomponent/utils/common'

interface RecentQuickActionProps {
  contact: RecentChat
  isDropdownOpen: boolean
  onToggleDropdown: (isOpen: boolean) => void
  onPinToggle: (e: React.MouseEvent) => void
  activeDropdown: RecentChat | null
  // Called whenever the user interacts with the menu (toggle or an item). The
  // parent uses this to suppress the iOS "ghost click" that lands on the chat
  // row beneath the menu right after a tap.
  onInteract?: () => void
}

const RecentQuickAction: React.FC<RecentQuickActionProps> = ({
  contact,
  isDropdownOpen,
  onToggleDropdown,
  onPinToggle,
  activeDropdown,
  onInteract,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { allow_archive_chat } = useAppSelector((state) => state.settings)
  const { toggleSmallSide } = useAppSelector((state) => state.messenger)
  const { recentChats } = useAppSelector((state) => state.chat)
  const [isMuteModalOpen, setIsMuteModalOpen] = useState(false)
  const [, setIsUnmuting] = useState(false)
  const toggleArchiveUser = mutations.useUnArchiveUser()
  const unmuteChatMutation = mutations.useUnmuteChat()
  const toggleFavoriteMutation = mutations.useToggleFavorite()
  const { refetch: refetchArchiveUser } = queries.useArchiveUser()
  const { refetch: refetchFavoriteUser } = queries.useGetFavorites()
  const toggleBlockMutation = mutations.useToggleBlock()
  const chatId = contact.chat_id
  const chatType = contact.chat_type || 'direct'
  const recentChat = recentChats.find((chat) => chat.chat_id === chatId && chat.chat_type === chatType)
  const isMuted = recentChat?.isMuted || false
  const isPinned = contact.isPinned || false

  useEffect(() => {
    const meetzyMain = getShadowRoot()?.querySelector('.meetzy-main')
    const appSidebar = getShadowRoot()?.querySelector('.app-sidebar')

    if (toggleSmallSide) {
      meetzyMain?.classList.add('small-sidebar')
      appSidebar?.classList.add('active')
      document.body.classList.add('sidebar-active', 'main-page')
    } else {
      meetzyMain?.classList.remove('small-sidebar')
      appSidebar?.classList.remove('active')
      document.body.classList.remove('sidebar-active', 'main-page')
    }
  }, [toggleSmallSide])

  const handleArchive = async (e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      const response = await toggleArchiveUser.mutateAsync({
        targetId: contact.chat_id,
        targetType:
          contact.chat_type === ChatType.group
            ? 'group'
            : contact.isBroadcast
              ? 'broadcast'
              : contact?.isAnnouncement
                ? 'announcement'
                : 'user',
      })

      const isArchived = response.action === 'archive'
      dispatch(setSelectedUser({ ...contact, isArchived }))
      if (contact.chat_id && contact.chat_type) {
        dispatch(
          toggleArchiveChat({
            chatId: contact.chat_id,
            chatType: contact.chat_type,
            isArchived,
          }),
        )
      }
      await refetchArchiveUser()
      onToggleDropdown(false)
    } catch (error: any) {
      console.error(error?.message || 'Failed to archive/unarchive chat')
    }
  }

  const handleUnmute = async () => {
    if (!chatId) return

    setIsUnmuting(true)
    try {
      const targetType = contact.chat_type === 'group' ? ChatType.group : ChatType.DM
      await unmuteChatMutation.mutateAsync({
        target_id: chatId,
        target_type: targetType === ChatType.group ? 'group' : activeDropdown?.isAnnouncement ? 'announcement' : 'user',
      })

      dispatch(
        toggleMuteChat({
          chatId,
          chatType: contact.chat_type || 'direct',
          isMuted: false,
        }),
      )
      onToggleDropdown(false)
    } catch (error: any) {
      console.error(error?.message || 'Failed to unmute chat')
    } finally {
      setIsUnmuting(false)
    }
  }

  const handleMuteClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isMuted) {
      handleUnmute()
    } else {
      setIsMuteModalOpen(true)
      onToggleDropdown(false)
    }
  }

  const handlePinClick = (e: React.MouseEvent) => {
    onPinToggle(e)
    onToggleDropdown(false)
  }

  const handleMuteSuccess = () => {
    dispatch(
      toggleMuteChat({
        chatId,
        chatType:
          activeDropdown?.chat_type === ChatType.group
            ? 'group'
            : activeDropdown?.isAnnouncement
              ? 'announcement'
              : 'user',
        isMuted: true,
      }),
    )
  }

  const handleFavourite = async () => {
    try {
      const response = await toggleFavoriteMutation.mutateAsync({
        targetId: activeDropdown?.chat_id,
        targetType:
          activeDropdown?.chat_type === ChatType.group
            ? 'group'
            : activeDropdown?.isAnnouncement
              ? 'announcement'
              : 'user',
      })

      const nextFavoriteState = response.isFavorite
      dispatch(setSelectedUser({ ...activeDropdown, isFavorite: nextFavoriteState }))
      if (activeDropdown?.chat_id && activeDropdown?.chat_type) {
        dispatch(
          setRecentChatFavoriteStatus({
            chatId: activeDropdown.chat_id,
            chatType: activeDropdown.chat_type === ChatType.group ? 'group' : 'direct',
            isFavorite: nextFavoriteState,
          }),
        )
      }
      await refetchFavoriteUser()
    } catch (error: any) {
      console.error(error?.message || 'Failed to Favorite/Unfavorite user')
    }
  }

  const handleBlockUser = async () => {
    try {
      const response = await toggleBlockMutation.mutateAsync({
        targetId: activeDropdown?.chat_id,
        block_type: activeDropdown?.chat_type == 'group' ? 'group' : 'user',
      })

      const nextBlockedState = response.action === 'block'

      dispatch(setSelectedUser({ ...activeDropdown, isBlocked: nextBlockedState }))
      if (activeDropdown?.chat_id) {
        dispatch(
          setRecentChatBlockedStatus({
            chatId: activeDropdown?.chat_id,
            isBlocked: nextBlockedState,
          }),
        )
      }
    } catch (error: any) {
      console.error(error?.message || 'Failed to block/unblock user')
    }
  }

  const dropdownOptions = getDropdownOptions(activeDropdown, isMuted, allow_archive_chat, isPinned, t, {
    handleFavourite,
    handlePinClick,
    handleMuteClick,
    handleArchive,
    handleBlockUser,
  })

  const toggle = (e?: React.MouseEvent) => {
    onInteract?.()
    if (!e) {
      onToggleDropdown(true)
      return
    }
    const nativeEvent = e.nativeEvent as any
    const path = getEventPath(nativeEvent)
    // ✅ ONLY toggle if click originated from DropdownToggle
    if (path && path.length > 0) {
      const isToggleClick = path.some(
        (el) =>
          el instanceof HTMLElement &&
          (el.classList.contains('dropdown_toggle') || el.classList.contains('dropdown-menu-custom')),
      )
      if (isToggleClick) {
        onToggleDropdown(true)
      }
    } else {
      setTimeout(() => {
        onToggleDropdown(false)
      }, 100)
    }
  }

  return (
    <Fragment>
      <Dropdown isOpen={isDropdownOpen} toggle={toggle} direction="down">
        <DropdownToggle
          className="dropdown_toggle"
          tag="span"
          data-toggle="dropdown"
          aria-expanded={isDropdownOpen}
          // Stop the click from bubbling to the chat row (which would open the
          // chat). reactstrap still runs its toggle after this onClick, so the
          // dropdown opens/closes normally.
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
          <SvgIcon className="more-option" iconId="cheveron-right" />
        </DropdownToggle>
        <DropdownWrapper end className="dropdown-menu-custom">
          {dropdownOptions
            .filter((item) => item.isVisible)
            .map((item) => (
              <DropdownItem
                key={item.id}
                onClick={(e) => {
                  e.stopPropagation()
                  // Mark the interaction so the parent suppresses the iOS "ghost
                  // click" that lands on the chat row beneath the menu right after.
                  onInteract?.()
                  item?.onClick?.(e)
                }}
                className={`dropdown-item-${item.id}`}
              >
                <SvgIcon className={'fill-secondary dropdown-menu-svg-icon'} iconId={item.iconId} />
                {item.label}
              </DropdownItem>
            ))}
        </DropdownWrapper>
      </Dropdown>
      <MuteChatModal
        isOpen={isMuteModalOpen}
        onClose={() => {
          setIsMuteModalOpen(false)
        }}
        contact={contact}
        onMuteSuccess={handleMuteSuccess}
      />
    </Fragment>
  )
}

export default RecentQuickAction
